import { Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { ImageFieldType } from '../utils/fieldconfig';
import { FieldContext } from '../utils/fieldcontext';
import { ApplicationStoreService } from './application-store.service';
import { SortPathService } from './sort-path.service';

/**
 * The ServerModelService maintains the "jsonModel" part in the NamespaceSettings stored in the Web Extension Part
 * and used by the server to pull the datasource data into the final data model.
 */
@Injectable({ providedIn: 'root' })
export class ServerModelService {
  constructor(private applicationStoreService: ApplicationStoreService, private sortPathService: SortPathService) {}

  create(namespace: string): any {
    const model: any = {};
    model[namespace] = {};
    return model;
  }

  async insert(fieldContext: FieldContext): Promise<void> {
    if (fieldContext.isScriptField || fieldContext.isAiAssistField) {
      return;
    }

    // assumption that all children of composite fields (e.g. address) are pulled at once on the server - aren't composite fields just a bullhorn thing?
    if (fieldContext.field.parent?.type === 'composite') {
      console.debug(`Skip inserting '${fieldContext.field.path}' into the server model as it is a composite child.`);
      return;
    }

    console.log(`Inserting ${fieldContext.entity.entity} (${fieldContext.entity.label}) for namespace ${fieldContext.namespace}.`);

    const nss = this.applicationStoreService.getNamespaceSettings(fieldContext.namespace)!;
    const model = nss.jsonModel[fieldContext.namespace];

    if (!fieldContext.isImageField) {
      this.insertInto(model, fieldContext.field.path);

      if (fieldContext.conditions) {
        fieldContext.conditions.forEach((condition) => {
          this.insertInto(model, condition.fieldPath);
        });
      }

      if (fieldContext.sortOption) {
        const sortPath =
          this.sortPathService.getModelSortFieldPath(fieldContext, fieldContext.sortOption) ??
          fieldContext.sortOption.fieldPath;
        if (sortPath) {
          this.insertInto(model, sortPath);
        }
      }
    } else {
      if (fieldContext.imageFieldConfig?.type === ImageFieldType.ENTITY_FIELD) {
        // the value will be a path like: Candidate.customTextBlock3
        this.insertInto(model, fieldContext.imageFieldConfig.value);
      }
    }

    await this.applicationStoreService.setNamespaceSettings(fieldContext.namespace, nss);
  }

  /**
   * Insert the path into the model used by the server to pull the field data.
   * @param namespace the namespace associated with the model (e.g. bullhorn)
   * @param path the field path to pull excluding the namespace (e.g. Candidate.id)
   */
  async insertPath(namespace: Namespace | string, path: string): Promise<void> {
    console.log(`Inserting ${path} for ${namespace} into server model.`);

    const nss = this.applicationStoreService.getNamespaceSettings(namespace)!;
    const model = nss.jsonModel[namespace];

    this.insertInto(model, path);

    await this.applicationStoreService.setNamespaceSettings(namespace, nss);
  }

  // NOTE: I might have messed this up. Should a to-one like peopleListItem.MartialStatus.ItemDisplayText work with an object (Map) like Einar had it?
  // NOTE: this is a real mess. We probably want a proper class to build up the structure in a sensible way and then transform it to the desired format on the server.
  private insertInto(model: any, fieldPath: string): void {
    const path: string[] = fieldPath.split('.');
    const state = { parent: null, entry: model };
    let pathIndex = 0;
    while (pathIndex < path.length) {
      // console.log(pathIndex, path[pathIndex]);

      if (typeof state.entry === 'string') {
        this.handleStringEntry(path, pathIndex, state);
      } else if (Array.isArray(state.entry)) {
        this.handleArrayEntry(path, pathIndex, state);
      } else {
        this.handleObjectMap(path, pathIndex, state);
      }

      pathIndex++;
    }
  }

  private handleStringEntry(path: string[], pathIndex: number, state: { entry: any; parent: any }): void {
    const ei = state.parent!.findIndex((f: any) => f === state.entry);
    state.parent!.splice(ei, 1); // remove the string entry in the parent / owner object
    const obj: any = [path[pathIndex]];
    state.parent!.push({ [state.entry]: obj }); // replace with an array (e.g. "documents": ["AttachmentName"])
    state.parent = obj;
    state.entry = obj[0];
  }

  private handleArrayEntry(path: string[], pathIndex: number, state: { entry: any; parent: any }): void {
    // the array can contain plain strings or complete objects
    let item = state.entry.find((k: any) => {
      if (typeof k === 'string') {
        return k === path[pathIndex];
      }
      return Object.keys(k).includes(path[pathIndex]);
    });

    if (!item) {
      if (pathIndex < path.length - 1) {
        const obj: any = [];
        state.entry.push({ [path[pathIndex]]: obj });
        state.entry = obj;
      } else {
        state.entry.push(path[pathIndex]);
      }
    } else {
      // if the item is an object then we want to use the value of that object pointed to by our path
      if (typeof item === 'object') {
        item = Object.entries(item as object).find(([k, v]) => k === path[pathIndex])![1];
      }
      state.parent = state.entry;
      state.entry = item;
    }
  }

  private handleObjectMap(path: string[], pathIndex: number, state: { entry: any; parent: any }): void {
    const entries = Object.entries(state.entry);

    let entry: any = entries.find(([k, v]) => k === path[pathIndex]);
    entry = entry ? entry[1] : undefined;

    if (!entry) {
      if (pathIndex < path.length - 1) {
        const obj: any = [];
        state.entry[path[pathIndex]] = obj;
        state.entry = obj;
      } else {
        state.entry.push(path[pathIndex]);
      }
    } else {
      state.entry = entry;
    }
  }
}
